<template>
  <div v-title="$route.meta.title"
       class="asr-check-form">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>录音风险管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/audio-asr-check/template' }">录音风险配置</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.meta.title }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <el-alert v-if="isReadonly"
              title="该配置已被工单引用，仅可查看，不可修改"
              type="warning"
              :closable="false"
              show-icon
              class="asr-check-form__alert" />

    <div class="asr-check-form__card">
      <div class="asr-check-form__card-header"
           @click="formExpanded = !formExpanded">
        <span>表单</span>
        <i :class="formExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
      </div>

      <el-form v-show="formExpanded"
               ref="form"
               v-loading="pageLoading"
               :model="form"
               :rules="formRules"
               size="medium"
               label-width="0"
               class="asr-check-form__body"
               @submit.native.prevent="onSubmit">
        <section class="asr-check-form__section">
          <h3 class="asr-check-form__section-title">基本信息</h3>

          <div class="asr-check-form__row">
            <label class="asr-check-form__label is-required">配置标题</label>
            <div class="asr-check-form__field">
              <el-form-item prop="title"
                            class="asr-check-form__form-item">
                <el-input v-model="form.title"
                          clearable
                          :disabled="isReadonly"
                          maxlength="255"
                          show-word-limit
                          placeholder="请输入配置标题"
                          class="asr-check-form__title-input" />
              </el-form-item>
            </div>
          </div>

          <div class="asr-check-form__row">
            <label class="asr-check-form__label is-required">提示词模板</label>
            <div class="asr-check-form__field">
              <div class="asr-check-form__prompt-toolbar">
                <div class="asr-check-form__prompt-vars">
                  <span class="asr-check-form__prompt-var"
                        v-pre>{{问题列表}}</span>
                  <span class="asr-check-form__prompt-var"
                        v-pre>{{ASR识别结果}}</span>
                  <span class="asr-check-form__prompt-var-tip">必填变量</span>
                </div>
                <el-button type="text"
                           size="mini"
                           icon="el-icon-refresh-left"
                           :disabled="isReadonly || !promptConfigLoaded"
                           @click="resetPromptTemplate">恢复默认</el-button>
              </div>
              <el-form-item prop="prompt_template"
                            class="asr-check-form__form-item">
                <el-input v-model="form.prompt_template"
                          type="textarea"
                          :rows="9"
                          :disabled="isReadonly"
                          placeholder="请输入 LLM 提示词模板" />
              </el-form-item>
              <el-collapse v-model="promptFixedExpanded"
                           class="asr-check-form__prompt-collapse">
                <el-collapse-item name="fixed">
                  <template slot="title">
                    <span class="asr-check-form__prompt-fixed-title">
                      <i class="el-icon-lock" />
                      固定输出格式（自动追加，不可修改）
                    </span>
                  </template>
                  <pre v-if="fixedPromptSuffix"
                       class="asr-check-form__prompt-fixed-content">{{ fixedPromptSuffix }}</pre>
                  <div v-else
                       class="asr-check-form__empty-hint">正在加载固定输出格式...</div>
                </el-collapse-item>
              </el-collapse>
            </div>
          </div>
        </section>

        <section class="asr-check-form__section">
          <div class="asr-check-form__section-head">
            <div>
              <h3 class="asr-check-form__section-title asr-check-form__section-title--inline">问题列表</h3>
              <p class="asr-check-form__section-desc">
                每条问题对应 LLM 返回 JSON 中的一项；「风险答案」用于后续判读是否触发风险。导入会替换当前列表，仅写入界面，需手动点击保存。
              </p>
            </div>
            <div class="asr-check-form__section-actions">
              <el-button size="small"
                         icon="el-icon-download"
                         @click="onExportQuestions">导出</el-button>
              <el-upload
                class="asr-check-form__import-upload"
                action=""
                accept=".xlsx,.xls"
                :show-file-list="false"
                :disabled="isReadonly || importingQuestions"
                :http-request="onImportQuestions">
                <el-button size="small"
                           icon="el-icon-upload2"
                           :loading="importingQuestions"
                           :disabled="isReadonly">导入</el-button>
              </el-upload>
              <el-button type="primary"
                         plain
                         size="small"
                         icon="el-icon-plus"
                         :disabled="isReadonly"
                         @click="addQuestion">添加问题</el-button>
            </div>
          </div>

          <div v-if="form.questions.length"
               class="asr-check-form__question-table">
            <div class="asr-check-form__question-head">
              <span class="asr-check-form__question-col is-index">序号</span>
              <span class="asr-check-form__question-col is-alias">风险名称</span>
              <span class="asr-check-form__question-col is-question">问题</span>
              <span class="asr-check-form__question-col is-risk">风险答案</span>
              <span class="asr-check-form__question-col is-action">操作</span>
            </div>
            <div v-for="(item, index) in form.questions"
                 :key="item._key"
                 class="asr-check-form__question-item">
              <span class="asr-check-form__question-col is-index">
                <span class="asr-check-form__question-badge">{{ index + 1 }}</span>
              </span>
              <div class="asr-check-form__question-col is-alias">
                <el-input v-model="item.alias"
                          placeholder="如：未提问"
                          clearable
                          maxlength="255"
                          size="small"
                          :disabled="isReadonly" />
              </div>
              <div class="asr-check-form__question-col is-question">
                <el-input v-model="item.question"
                          type="textarea"
                          :autosize="{ minRows: 1, maxRows: 8 }"
                          placeholder="请输入问题内容"
                          size="small"
                          :disabled="isReadonly" />
              </div>
              <div class="asr-check-form__question-col is-risk">
                <el-input v-model="item.risk_answer"
                          placeholder="如：否 / 无风险"
                          clearable
                          size="small"
                          :disabled="isReadonly" />
              </div>
              <span class="asr-check-form__question-col is-action">
                <el-button type="text"
                           icon="el-icon-delete"
                           class="asr-check-form__question-remove"
                           :disabled="isReadonly || form.questions.length <= 1"
                           @click="removeQuestion(index)" />
              </span>
            </div>
          </div>
          <div v-else
               class="asr-check-form__empty-hint">
            暂无问题，请点击「添加问题」
          </div>
        </section>

        <div class="asr-check-form__footer">
          <span class="asr-check-form__footer-tip">预览使用固定 ASR 示例拼接完整提示词</span>
          <div class="asr-check-form__footer-actions">
            <el-button icon="el-icon-view"
                       :loading="previewLoading"
                       :disabled="submitting || !promptConfigLoaded"
                       @click="onPreviewPrompt">预览提示词</el-button>
            <el-button @click="$router.push('/audio-asr-check/template')">取消</el-button>
            <el-button v-if="!isReadonly"
                       type="primary"
                       native-type="submit"
                       :loading="submitting">保存</el-button>
          </div>
        </div>
      </el-form>
    </div>

    <el-dialog title="LLM 提示词预览"
               :visible.sync="promptPreviewVisible"
               width="720px"
               append-to-body>
      <div v-if="previewLoading"
           class="asr-check-form__empty-hint">正在生成提示词...</div>
      <div v-else-if="previewPrompt"
           class="asr-check-form__prompt-preview-wrap">
        <pre class="asr-check-form__prompt-preview-content">{{ previewPrompt }}</pre>
      </div>
      <div v-else
           class="asr-check-form__empty-hint">暂无可预览的提示词</div>
      <span slot="footer">
        <el-button @click="promptPreviewVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  createEmptyQuestion,
  validatePromptTemplate,
} from '../audio-asr-check.prompt'
import {
  QUESTION_EXPORT_HEADERS,
  buildQuestionExportRows,
  parseQuestionImportWorkbook,
  readFileAsArrayBuffer,
} from '../audio-asr-check.question-io'

let questionKey = 1

function wrapQuestion (item) {
  return {
    _key: item.id ? `id-${item.id}` : questionKey++,
    id: item.id || undefined,
    alias: item.alias || '',
    question: item.question || '',
    risk_answer: item.risk_answer || '',
  }
}

export default {
  data () {
    const checkPrompt = (rule, value, callback) => {
      if (!value || !String(value).trim()) {
        callback(new Error('请输入提示词模板'))
        return
      }
      if (!validatePromptTemplate(value)) {
        callback(new Error('提示词模板必须包含 {{问题列表}} 与 {{ASR识别结果}}'))
        return
      }
      callback()
    }
    return {
      formExpanded: true,
      pageLoading: false,
      submitting: false,
      previewLoading: false,
      importingQuestions: false,
      promptConfigLoaded: false,
      promptPreviewVisible: false,
      previewPrompt: '',
      promptFixedExpanded: [],
      defaultPromptBody: '',
      defaultQuestions: [],
      fixedPromptSuffix: '',
      isReferenced: false,
      form: {
        title: '',
        prompt_template: '',
        // 新建默认题目只从后端 prompt_config 拉取，避免前后端各维护一份
        questions: [],
      },
      formRules: {
        title: [
          { required: true, message: '请输入配置标题', trigger: 'blur' },
          { max: 255, message: '配置标题最多 255 个字符', trigger: 'blur' },
        ],
        prompt_template: [
          { required: true, validator: checkPrompt, trigger: 'blur' },
        ],
      },
    }
  },
  computed: {
    templateId () {
      return this.$route.params.id ? Number(this.$route.params.id) : 0
    },
    isEdit () {
      return !!this.templateId
    },
    isReadonly () {
      return this.isEdit && this.isReferenced
    },
  },
  created () {
    this.loadPromptConfig()
    if (this.isEdit) {
      this.loadDetail()
    }
  },
  methods: {
    loadPromptConfig () {
      request.common('getAudioAsrCheckPromptConfig').then(({ data }) => {
        this.defaultPromptBody = data.default_template_body || ''
        this.fixedPromptSuffix = data.fixed_template_suffix || ''
        this.defaultQuestions = Array.isArray(data.default_questions) ? data.default_questions : []
        this.promptConfigLoaded = true
        if (!this.isEdit) {
          if (!this.form.prompt_template) {
            this.form.prompt_template = this.defaultPromptBody
          }
          if (this.defaultQuestions.length) {
            this.form.questions = this.defaultQuestions.map(wrapQuestion)
          } else if (!this.form.questions.length) {
            this.form.questions = [createEmptyQuestion(questionKey++)]
          }
        }
      }).catch(() => {
        this.promptConfigLoaded = true
        if (!this.isEdit && !this.form.questions.length) {
          this.form.questions = [createEmptyQuestion(questionKey++)]
        }
      })
    },
    loadDetail () {
      this.pageLoading = true
      request.common('getAudioAsrCheckTemplate', { id: this.templateId }).then(({ data }) => {
        this.isReferenced = Number(data.is_referenced) === 1
        this.form.title = data.title || ''
        this.form.prompt_template = data.prompt_template || ''
        this.form.questions = (data.questions || []).map(wrapQuestion)
        if (!this.form.questions.length) {
          this.form.questions = [createEmptyQuestion(questionKey++)]
        }
      }).finally(() => {
        this.pageLoading = false
      })
    },
    resetPromptTemplate () {
      this.form.prompt_template = this.defaultPromptBody
      if (!this.isEdit && this.defaultQuestions.length) {
        this.form.questions = this.defaultQuestions.map(wrapQuestion)
      }
    },
    addQuestion () {
      this.form.questions.push(createEmptyQuestion(questionKey++))
    },
    removeQuestion (index) {
      if (this.form.questions.length <= 1) {
        return
      }
      this.form.questions.splice(index, 1)
    },
    onExportQuestions () {
      let rows = buildQuestionExportRows(this.form.questions)
      const title = (this.form.title || '录音风险配置').trim()
      if (!rows.length) {
        rows = [
          {
            alias: '未提问',
            question: '录音中是否有询问粮油？',
            risk_answer: '否',
          },
          {
            alias: '必填',
            question: '必填',
            risk_answer: '必填',
          },
        ]
        this.$export(`${title}-问题导入模板`, rows, {
          fileHeader: QUESTION_EXPORT_HEADERS,
        })
        return
      }
      this.$export(`${title}-问题列表`, rows, {
        fileHeader: QUESTION_EXPORT_HEADERS,
      })
    },
    onImportQuestions (requestOption) {
      const file = requestOption && requestOption.file
      if (!file) {
        return
      }
      if (this.isReadonly) {
        this.$message.warning('当前配置不可修改')
        return
      }
      this.importingQuestions = true
      readFileAsArrayBuffer(file).then((buffer) => {
        const result = parseQuestionImportWorkbook(buffer)
        if (result.error) {
          this.$message.error(result.error)
          return
        }
        // 过滤说明行（风险名称为“必填”之类）
        const questions = result.questions.filter((item) => {
          const alias = (item.alias || '').trim()
          const question = (item.question || '').trim()
          if (alias === '必填' && question === '必填') {
            return false
          }
          return true
        })
        if (!questions.length) {
          this.$message.error('未解析到有效问题，请检查数据行')
          return
        }
        // 直接替换界面数据，不带旧 question.id，手动保存后才会落库
        this.form.questions = questions.map((item) => createEmptyQuestion(questionKey++, item))
        this.$message.success(`已导入 ${questions.length} 条问题（尚未保存，请确认后点击保存）`)
      }).catch((error) => {
        this.$message.error(error && error.message ? error.message : '导入失败')
      }).finally(() => {
        this.importingQuestions = false
      })
    },
    buildPayload () {
      return {
        title: (this.form.title || '').trim(),
        prompt_template: (this.form.prompt_template || '').trim(),
        questions: this.form.questions.map((item, index) => {
          const row = {
            seq: index + 1,
            alias: (item.alias || '').trim(),
            question: (item.question || '').trim(),
            risk_answer: (item.risk_answer || '').trim(),
          }
          if (item.id) {
            row.id = item.id
          }
          return row
        }),
      }
    },
    validateQuestionsLocal () {
      const questions = this.form.questions || []
      if (!questions.length) {
        this.$message.error('请至少配置一个问题')
        return false
      }
      for (let i = 0; i < questions.length; i++) {
        const no = i + 1
        const item = questions[i]
        if (!(item.alias || '').trim()) {
          this.$message.error(`第${no}个风险名称不能为空`)
          return false
        }
        if (!(item.question || '').trim()) {
          this.$message.error(`第${no}个问题内容不能为空`)
          return false
        }
        if (!(item.risk_answer || '').trim()) {
          this.$message.error(`第${no}个问题风险答案不能为空`)
          return false
        }
      }
      if (!validatePromptTemplate(this.form.prompt_template)) {
        this.$message.error('提示词模板必须包含 {{问题列表}} 与 {{ASR识别结果}}')
        return false
      }
      return true
    },
    onPreviewPrompt () {
      if (!this.validateQuestionsLocal()) {
        return
      }
      this.previewLoading = true
      this.promptPreviewVisible = true
      this.previewPrompt = ''
      request.common('previewAudioAsrCheckPrompt', this.buildPayload()).then(({ data }) => {
        this.previewPrompt = data.prompt || ''
      }).finally(() => {
        this.previewLoading = false
      })
    },
    onSubmit () {
      if (this.isReadonly) {
        return
      }
      this.$refs.form.validate((valid) => {
        if (!valid) {
          return
        }
        if (!this.validateQuestionsLocal()) {
          return
        }
        const payload = this.buildPayload()
        this.submitting = true
        const req = this.isEdit
          ? request.common('updateAudioAsrCheckTemplate', { id: this.templateId, ...payload })
          : request.common('createAudioAsrCheckTemplate', payload)
        req.then(() => {
          this.$message.success('保存成功')
          this.$router.push('/audio-asr-check/template')
        }).finally(() => {
          this.submitting = false
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.asr-check-form {
  &__alert {
    margin-bottom: 12px;
  }

  &__card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  &__card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px;
    border-bottom: 1px solid #ebeef5;
    cursor: pointer;
    user-select: none;
    color: #606266;
    font-size: 14px;
    font-weight: 500;

    i {
      color: #909399;
    }
  }

  &__body {
    padding: 20px 24px 0;
  }

  &__section + &__section {
    margin-top: 24px;
  }

  &__section-title {
    margin: 0 0 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ebeef5;
    color: #303133;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;

    &--inline {
      margin-bottom: 4px;
      padding-bottom: 0;
      border-bottom: none;
    }
  }

  &__section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ebeef5;
  }

  &__section-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    flex-shrink: 0;
  }

  &__import-upload {
    display: inline-block;
  }

  &__section-desc {
    margin: 0;
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
    font-weight: normal;
  }

  &__row {
    display: grid;
    grid-template-columns: minmax(120px, 16.666%) minmax(0, 1fr);
    gap: 16px;
    align-items: start;
    margin-bottom: 16px;
  }

  &__label {
    padding-top: 8px;
    color: #606266;
    font-size: 14px;
    line-height: 1.5;

    &.is-required::before {
      content: '*';
      color: #f56c6c;
      margin-right: 4px;
    }
  }

  &__field {
    min-width: 0;
  }

  &__form-item {
    margin-bottom: 0;
  }

  &__title-input {
    max-width: 480px;
  }

  &__prompt-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__prompt-vars {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__prompt-var {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    background: #ecf5ff;
    color: #409eff;
    font-size: 12px;
  }

  &__prompt-var-tip {
    color: #909399;
    font-size: 12px;
  }

  &__prompt-collapse {
    margin-top: 8px;
  }

  &__prompt-fixed-title {
    color: #606266;
    font-size: 13px;
  }

  &__prompt-fixed-content,
  &__prompt-preview-content {
    margin: 0;
    padding: 12px;
    background: #f5f7fa;
    border-radius: 4px;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px;
    line-height: 1.6;
    max-height: 420px;
    overflow: auto;
  }

  &__question-table {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    overflow: hidden;
  }

  &__question-head,
  &__question-item {
    display: flex;
    gap: 8px;
    padding: 10px 12px;
  }

  &__question-head {
    align-items: center;
    background: #f5f7fa;
    color: #909399;
    font-size: 12px;
    line-height: 1;
    min-height: 36px;
    box-sizing: border-box;
  }

  &__question-item {
    align-items: flex-start;
    border-top: 1px solid #ebeef5;
  }

  &__question-col {
    &.is-index {
      width: 36px;
      flex: none;
    }

    &.is-alias {
      width: 160px;
      flex: none;
    }

    &.is-question {
      flex: 1;
      min-width: 220px;
    }

    &.is-risk {
      width: 180px;
      flex: none;
    }

    &.is-action {
      width: 40px;
      flex: none;
      text-align: center;
    }
  }

  // 仅数据行对齐输入框；表头不加 padding，避免列文字上下错位
  &__question-item &__question-col {
    &.is-index {
      padding-top: 6px;
    }

    &.is-action {
      padding-top: 4px;
    }
  }

  &__question-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #409eff;
    color: #fff;
    font-size: 12px;
  }

  &__question-remove {
    color: #f56c6c;
  }

  &__empty-hint {
    color: #909399;
    font-size: 13px;
    padding: 12px 0;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 8px -24px 0;
    padding: 12px 24px;
    background: #f5f7fa;
    border-top: 1px solid #ebeef5;
    border-radius: 0 0 8px 8px;
  }

  &__footer-tip {
    color: #909399;
    font-size: 12px;
  }

  &__footer-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .asr-check-form {
    &__row {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    &__label {
      padding-top: 0;
    }

    &__section-head {
      flex-direction: column;
      align-items: stretch;
    }

    &__section-actions {
      justify-content: flex-start;
    }

    &__footer {
      flex-direction: column;
      align-items: stretch;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/audio-asr-check/pages/AudioAsrCheckTemplateForm.vue